/**
 * Mes progrès — tableau de bord pédagogique de l'utilisateur.
 *
 * Récupère les compétences de lecture et règles tajwīd suivies par
 * `course_exercises` (via `coursesApi.myProgress`) plus les
 * recommandations issues de `coursesApi.recommendations`.
 *
 * Pas d'IA, pas d'invention. Tout est calculé côté backend à partir des
 * tentatives de l'utilisateur.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Sparkles, BookOpen, Compass, AlertTriangle,
  CheckCircle2, GraduationCap, RefreshCcw, Flame, Trophy,
} from 'lucide-react';
import { GlassCard, PageLoading, SectionHeader } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { coursesApi, tajwidApi, learningApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_LABEL = {
  not_started: { fr: 'Non commencé', cls: 'text-text-muted bg-surface-elevated' },
  learning:    { fr: 'En cours',     cls: 'text-amber-300 bg-amber-500/10' },
  needs_review:{ fr: 'À revoir',     cls: 'text-rose-300 bg-rose-500/10' },
  mastered:    { fr: 'Maîtrisé',     cls: 'text-emerald-300 bg-emerald-500/15' },
};

const SKILL_LABEL = {
  letters: 'Lettres', letter_shapes: 'Formes des lettres',
  harakat: 'Ḥarakāt', tanwin: 'Tanwīn', sukun: 'Sukūn', shadda: 'Shadda',
  madd: 'Voyelles longues (madd)', syllables: 'Syllabes',
  words: 'Mots coraniques', ayah_segments: 'Segments de versets',
};

const PercentBar = ({ value }) => (
  <div className="h-1.5 w-full bg-background/60 rounded overflow-hidden">
    <div
      className="h-full bg-gradient-to-r from-primary/70 to-emerald-400/80 transition-all"
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
);

const SummaryStat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-lg border border-border bg-surface-elevated/40 p-4 flex items-start gap-3">
    <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${accent || 'bg-primary/15 text-primary'}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1">
      <div className="text-xs uppercase tracking-wider text-text-muted">{label}</div>
      <div className="text-xl font-serif text-text-primary mt-0.5">{value}</div>
    </div>
  </div>
);

const SkillRow = ({ skill, accuracy = 0, status = 'not_started', attempts = 0, label, href }) => {
  const meta = STATUS_LABEL[status] || STATUS_LABEL.not_started;
  return (
    <div
      className="rounded-lg border border-border bg-surface-elevated/30 p-3 space-y-2"
      data-testid={`progress-skill-${skill}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        <span className={`text-[11px] px-2 py-0.5 rounded ${meta.cls}`}>{meta.fr}</span>
      </div>
      <PercentBar value={Math.round((accuracy || 0) * 100)} />
      <div className="flex items-center justify-between text-xs text-text-muted">
        <span>{Math.round((accuracy || 0) * 100)}% · {attempts} tentative{attempts > 1 ? 's' : ''}</span>
        {href && (
          <Link to={href} className="text-primary hover:underline">Pratiquer →</Link>
        )}
      </div>
    </div>
  );
};

export const MesProgres = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [recos, setRecos] = useState([]);
  const [rules, setRules] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    let cancel = false;
    setLoading(true);
    Promise.all([
      coursesApi.myProgress().then((r) => r.data).catch(() => null),
      coursesApi.recommendations().then((r) => r.data?.recommendations || []).catch(() => []),
      tajwidApi.getRules().then((r) => r.data || []).catch(() => []),
      learningApi.progressSummary().then((r) => r.data?.items || []).catch(() => []),
    ]).then(([prog, recs, rls, currs]) => {
      if (cancel) return;
      setData(prog);
      setRecos(recs);
      setRules(rls);
      setCurricula(currs);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [isAuthenticated]);

  const ruleLabel = (code) => rules.find((r) => r.rule === code)?.label_fr || code;

  if (loading) return <PageLoading />;

  if (!isAuthenticated) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="mes-progres-anonymous">
        <SectionHeader
          overline="Mes progrès"
          title="Connectez-vous pour suivre vos progrès"
          subtitle="Le suivi des compétences (lecture & tajwīd) est personnel et lié à votre compte."
        />
        <div className="flex gap-3 mt-6">
          <Button onClick={() => navigate('/login')} data-testid="mes-progres-login">Se connecter</Button>
          <Button variant="outline" onClick={() => navigate('/register')}>Créer un compte</Button>
        </div>
      </div>
    );
  }

  const reading = data?.reading || [];
  const tajweed = data?.tajweed || [];
  const summary = data?.summary || { reading: {}, tajweed: {} };
  const recent = data?.recentAttempts || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="mes-progres-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/cours')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="mes-progres-back"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux cours
      </Button>

      <SectionHeader
        overline="Mon parcours"
        title="Mes progrès"
        subtitle="Vue d'ensemble de votre apprentissage de la lecture et du tajwīd, avec des recommandations personnalisées."
      />

      {/* Compteur d'étapes maîtrisées — agrégé sur tous les curriculums.
          Conçu comme bandeau d'encouragement plutôt que stat froide. */}
      {curricula.length > 0 && (() => {
        const totals = curricula.reduce((acc, { summary: s }) => ({
          mastered: acc.mastered + (s.completedCompetencies || 0),
          total:    acc.total + (s.totalCompetencies || 0),
        }), { mastered: 0, total: 0 });
        const pct = totals.total
          ? Math.round((totals.mastered / totals.total) * 100)
          : 0;
        // Pick the next competency from the curriculum that has the lowest
        // progressPercent (≠ 100) so the banner points to the most
        // reachable next milestone.
        const focus = [...curricula]
          .filter(({ summary: s }) => (s.currentCompetencyId
                                        && s.progressPercent < 100))
          .sort((a, b) => (a.summary.progressPercent || 0)
                            - (b.summary.progressPercent || 0))[0];
        return (
          <div
            className="mt-6 rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-emerald-500/5 to-transparent p-4 flex items-center gap-4 flex-wrap"
            data-testid="mes-progres-mastery-counter"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0">
              <Trophy className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <p
                className="text-base font-serif text-text-primary"
                data-testid="mes-progres-mastery-counter-text"
              >
                <span className="text-emerald-300 font-semibold">
                  {totals.mastered}
                </span>
                {' / '}
                <span>{totals.total}</span>
                {' compétences maîtrisées'}
                {pct > 0 && (
                  <span className="text-text-muted text-sm ml-2">· {pct}%</span>
                )}
              </p>
              {focus?.summary?.currentCompetencyTitle ? (
                <p className="text-xs text-text-muted mt-1">
                  Prochaine étape :{' '}
                  <span
                    className="text-text-primary"
                    data-testid="mes-progres-mastery-next"
                  >
                    {focus.summary.currentCompetencyTitle}
                  </span>
                  {' '}({focus.curriculum.title?.fr || 'parcours'})
                </p>
              ) : totals.mastered === totals.total && totals.total > 0 ? (
                <p className="text-xs text-emerald-300 mt-1">
                  Tous les parcours sont terminés. Bravo !
                </p>
              ) : (
                <p className="text-xs text-text-muted mt-1">
                  Lancez-vous sur un parcours pour commencer à débloquer des étapes.
                </p>
              )}
            </div>
            <div className="w-full sm:w-48">
              <div className="h-2 w-full bg-background/60 rounded overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400/80 to-primary/80 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Curriculum overview cards */}
      {curricula.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6" data-testid="mes-progres-curricula">
          {curricula.map(({ curriculum, summary }) => {
            const targetHref = curriculum.type === 'tajweed' ? '/cours/tajwid' : '/cours/lecture';
            return (
              <Link
                key={curriculum.id}
                to={targetHref}
                className="rounded-lg border border-border bg-surface-elevated/40 p-4 hover:border-primary/50 transition-colors"
                data-testid={`mes-progres-curriculum-${curriculum.type}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm text-text-primary font-serif">{curriculum.title?.fr}</h3>
                  <Badge variant="outline" className="text-[10px]">
                    {summary.completedCompetencies}/{summary.totalCompetencies}
                  </Badge>
                </div>
                <div className="mt-2 h-2 w-full bg-background/60 rounded overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary/70 to-emerald-400/80"
                    style={{ width: `${summary.progressPercent}%` }}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-text-muted">
                  <span>{summary.progressPercent}% complété</span>
                  <span className="text-primary inline-flex items-center">
                    Ouvrir <ArrowRight className="w-3 h-3 ml-1" />
                  </span>
                </div>
                {summary.status === 'completed' && (
                  <p className="text-[11px] text-emerald-300 mt-2">Parcours terminé · bravo !</p>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="mes-progres-summary">
        <SummaryStat
          icon={BookOpen}
          label="Lecture · maîtrisées"
          value={`${summary.reading?.mastered || 0} / ${summary.reading?.total || 0}`}
          accent="bg-emerald-500/15 text-emerald-300"
        />
        <SummaryStat
          icon={Sparkles}
          label="Tajwīd · maîtrisées"
          value={`${summary.tajweed?.mastered || 0} / ${summary.tajweed?.total || 0}`}
          accent="bg-emerald-500/15 text-emerald-300"
        />
        <SummaryStat
          icon={AlertTriangle}
          label="À revoir"
          value={(summary.reading?.needs_review || 0) + (summary.tajweed?.needs_review || 0)}
          accent="bg-rose-500/15 text-rose-300"
        />
        <SummaryStat
          icon={Flame}
          label="Précision globale"
          value={`${Math.round(((summary.reading?.accuracy || 0) + (summary.tajweed?.accuracy || 0)) * 50)}%`}
          accent="bg-primary/15 text-primary"
        />
      </div>

      {/* Recommendations */}
      {recos.length > 0 && (
        <GlassCard className="mt-6 space-y-3" data-testid="mes-progres-recos">
          <h2 className="overline flex items-center gap-2">
            <Compass className="w-4 h-4 text-primary" /> Recommandations
          </h2>
          <p className="text-xs text-text-muted">
            Basées sur vos dernières tentatives. Travaillez d'abord les points faibles.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recos.map((r, i) => (
              <Link
                key={i}
                to={r.href || '/cours'}
                className="rounded-lg border border-border bg-surface-elevated/40 p-3 hover:border-primary/50 transition-colors flex items-start gap-3"
                data-testid={`mes-progres-reco-${r.kind}-${r.skill || r.ruleId}`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                  r.kind === 'review' ? 'bg-rose-500/15 text-rose-300' : 'bg-primary/15 text-primary'
                }`}>
                  {r.kind === 'review' ? <RefreshCcw className="w-4 h-4" /> : <GraduationCap className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text-primary truncate">
                    {r.section === 'reading'
                      ? SKILL_LABEL[r.skill] || r.skill
                      : ruleLabel(r.ruleId)}
                  </div>
                  <div className="text-xs text-text-muted">{r.reason}</div>
                </div>
              </Link>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Reading skills */}
      <GlassCard className="mt-6 space-y-3" data-testid="mes-progres-reading">
        <h2 className="overline flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary" /> Lecture · compétences
        </h2>
        {reading.length === 0 ? (
          <p className="text-sm text-text-muted">
            Aucune compétence enregistrée pour le moment.{' '}
            <Link to="/cours/lecture" className="text-primary hover:underline">
              Commencez les exercices →
            </Link>
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {reading.map((s) => (
              <SkillRow
                key={s.skill}
                skill={s.skill}
                label={SKILL_LABEL[s.skill] || s.skill}
                accuracy={s.accuracy}
                status={s.status}
                attempts={s.attempts}
                href={`/cours/lecture?focus=${s.skill}`}
              />
            ))}
          </div>
        )}
      </GlassCard>

      {/* Tajwid skills */}
      <GlassCard className="mt-6 space-y-3" data-testid="mes-progres-tajweed">
        <h2 className="overline flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" /> Tajwīd · règles
        </h2>
        {tajweed.length === 0 ? (
          <p className="text-sm text-text-muted">
            Aucune règle pratiquée.{' '}
            <Link to="/cours/tajwid" className="text-primary hover:underline">
              Découvrir les règles →
            </Link>
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tajweed.map((s) => (
              <SkillRow
                key={s.ruleId}
                skill={s.ruleId}
                label={ruleLabel(s.ruleId)}
                accuracy={s.accuracy}
                status={s.status}
                attempts={s.attempts}
                href={`/cours/tajwid/${s.ruleId}`}
              />
            ))}
          </div>
        )}
      </GlassCard>

      {/* Recent attempts */}
      {recent.length > 0 && (
        <GlassCard className="mt-6 space-y-3" data-testid="mes-progres-recent">
          <h2 className="overline flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary" /> Activité récente
          </h2>
          <div className="space-y-2">
            {recent.slice(0, 8).map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between text-xs text-text-secondary border-b border-border/40 pb-2"
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                    a.isCorrect ? 'bg-emerald-400' : 'bg-rose-400'
                  }`} />
                  <span className="text-text-primary">
                    {a.section === 'reading'
                      ? (SKILL_LABEL[a.skill] || a.skill || 'Exercice')
                      : ruleLabel(a.ruleId || a.expectedRuleId)}
                  </span>
                  <Badge variant="outline" className="text-[10px]">{a.section}</Badge>
                </div>
                <span className="text-text-muted">
                  {new Date(a.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <Button onClick={() => navigate('/cours/lecture')} data-testid="mes-progres-cta-lecture">
          <BookOpen className="w-4 h-4 mr-2" /> Continuer la lecture
        </Button>
        <Button variant="outline" onClick={() => navigate('/cours/tajwid')} data-testid="mes-progres-cta-tajwid">
          <Sparkles className="w-4 h-4 mr-2" /> Pratiquer le tajwīd
        </Button>
      </div>
    </div>
  );
};

export default MesProgres;
